import { Button } from '@shared/components/ui/button';
import { cn } from '@shared/utils/utils';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useEmptyViewTranslations } from './i18n';
import {
  CouriersIcon,
  DefaultIcon,
  HandsIcon,
  LocationMovementsIcon,
  LocationsIcon,
  MerchandiseIcon,
  OrderPreparationIcon,
  OrdersIcon,
  OrdersShipmentIcon,
  ProductsIcon,
  ReturnedIcon,
  RolesIcon,
  ShopsIcon,
  StockMovementsIcon,
} from './icons';
import { EmptyViewProps } from './types';

/**
 * Componente EmptyView
 * Muestra un estado vac├¡o con ├¡cono, t├¡tulo, descripci├│n y acci├│n opcional
 * Los ├¡conos se adaptan autom├íticamente al tema light/dark usando currentColor
 * Tama├▒o de ├¡cono responsive: 120px en desktop, 100px en mobile
 */
export const EmptyView: React.FC<EmptyViewProps> = ({
  type = 'emptyState',
  title,
  description,
  icon = 'default',
  action,
  customAction,
  className,
  titleClassName,
  descriptionClassName,
  iconClassName,
  iconSize = 150, // Tamaño base para desktop
}) => {
  // ≡ƒÄ» Obtener idioma actual del sitio autom├íticamente
  const { i18n } = useTranslation();
  const translations = useEmptyViewTranslations(i18n.language);

  // Obtener textos (personalizados o de i18n)
  const displayTitle = title || (type === 'noResults' ? translations.noResults.title : translations.emptyState.title);
  const displayDescription =
    description || (type === 'noResults' ? translations.noResults.description : translations.emptyState.description);

  // Mapeo de ├¡conos
  const iconMap = {
    default: DefaultIcon,
    returned: ReturnedIcon,
    'location-movements': LocationMovementsIcon,
    'stock-movements': StockMovementsIcon,
    merchandise: MerchandiseIcon,
    locations: LocationsIcon,
    'order-preparation': OrderPreparationIcon,
    'orders-shipment': OrdersShipmentIcon,
    orders: OrdersIcon,
    products: ProductsIcon,
    shops: ShopsIcon,
    roles: RolesIcon,
    hands: HandsIcon,
    couriers: CouriersIcon,
  };

  // Renderizar ├¡cono
  const renderIcon = () => {
    if (!icon) return null;

    // Si es un ReactNode personalizado
    if (React.isValidElement(icon)) {
      return <div className={cn('text-card-border', iconClassName)}>{icon}</div>;
    }

    // Si es un string, usar el mapeo de ├¡conos
    if (typeof icon === 'string') {
      const IconComponent = iconMap[icon as keyof typeof iconMap];
      if (IconComponent) {
        return (
          <div className={cn('text-card-border pb-4', iconClassName)}>
            {/* 🎯 Ícono responsive: 150px desktop, 100px mobile usando CSS */}
            <IconComponent size={iconSize} />
          </div>
        );
      }
    }

    return null;
  };

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center min-h-[80vh] py-6 px-6 text-center space-y-4 mx-auto',
        className,
      )}
    >
      {/* ├ìcono */}
      {renderIcon()}

      {/* T├¡tulo */}
      <h3 className={cn('text-lg font-bold text-title sm:text-xl', titleClassName)}>{displayTitle}</h3>

      {/* Descripci├│n */}
      {displayDescription && (
        <p className={cn('text-complementary-text text-sm sm:text-base', descriptionClassName)}>{displayDescription}</p>
      )}

      {/* Bot├│n de acci├│n - Prioridad: customAction > action */}
      {customAction && <div className="mt-4">{customAction}</div>}

      {!customAction && action && (
        <Button
          onClick={action.onClick}
          variant="primary"
          size="default"
          className="gap-2 transition-colors mx-auto w-full mt-4"
          data-testid={action.testId}
        >
          {action.label}
        </Button>
      )}
    </div>
  );
};
